import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';

import { Campaign, CampaignStats, CampaignEmail } from '../models/campaign';

export type CampaignEmailFilter = 'all' | 'opens' | 'complaints' | 'unsubscribes' | 'bounces';

export class CampaignService extends BaseService<Campaign> {
  override model = Campaign;
  override modelName = 'campaign';
  override modelLabel = 'Campaign';

  async filtered({
    page = 1,
    filters = {},
  }: {
    page?: number;
    filters?: Record<string, any>;
  } = {}): Promise<ApiListResponse<Campaign>> {
    const query: Record<string, string> = { page: page.toString() };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== null) {
        // Laravel resolves dot notation from a nested query array. Literal dots
        // are normalised to underscores by PHP before Laravel receives them.
        query[`filter[${key}]`] = String(value);
      }
    }

    const request = new ApiRequest({
      method: 'GET',
      url: this.modelBaseUrl,
      query,
    });

    return await this.requestList(request, Campaign);
  }

  async stats(
    campaign: Campaign,
  ): Promise<ApiResponse<CampaignStats>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${campaign.objectID()}/stats`,
    });

    return await this.requestItem(request, CampaignStats);
  }

  async sendPreview(
    campaign: Campaign,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${campaign.objectID()}/preview`,
    });

    return await this.requestItem(request, Campaign);
  }

  async send(
    campaign: Campaign,
    { scheduledDate }: { scheduledDate?: string | null } = {},
  ): Promise<ApiResponse<any>> {
    const query: Record<string, string> = {};

    if (scheduledDate) {
      query.scheduledDate = scheduledDate;
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${campaign.objectID()}/send`,
      query,
    });

    return await this.requestItem(request, Campaign);
  }

  async emails(
    campaign: Campaign,
    { type, page = 1, limit }: { type: CampaignEmailFilter; page?: number; limit?: number },
  ): Promise<ApiListResponse<CampaignEmail>> {
    const query: Record<string, string> = {
      page: page.toString(),
      type,
    };

    if (limit) {
      query.limit = limit.toString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${campaign.objectID()}/emails`,
      query,
    });

    return await this.requestList(request, CampaignEmail);
  }

  async calculateCredits(
    campaign: Campaign,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${campaign.objectID()}/calculateCredits`,
    });

    return await this.request(request);
  }

  async addAttachment(
    campaign: Campaign,
    { attachment, idPhoto }: { attachment?: File; idPhoto?: number },
    onUploadProgress?: (progressEvent: any) => void,
  ): Promise<ApiResponse<Campaign>> {
    const formData = new FormData();

    if (attachment) {
      formData.append('attachment', attachment);
    }

    if (idPhoto) {
      formData.append('idPhoto', idPhoto.toString());
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${campaign.objectID()}/addAttachment`,
      data: formData,
      onUploadProgress,
    });

    return await this.requestItem(request, Campaign);
  }

  async removeAttachment(
    campaign: Campaign,
    attachment: { guid: string },
  ): Promise<ApiResponse<Campaign>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${campaign.objectID()}/removeAttachment`,
      query: { guid: attachment.guid },
    });

    return await this.requestItem(request, Campaign);
  }
}
